import ArrowUp from 'assets/svg/icons/ArrowUp';
import AttachmentsIcon from 'assets/svg/icons/Attachments';
import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import { Tooltip } from 'components/atoms/Tooltip';
import { AnimatePresence, motion } from 'motion/react';
import type { FC } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { logAmplitude } from 'utils/amplitude';
import { WEB_AIASSISTANT_CLICKTEXTAREA } from 'utils/amplitude/events/ai.amplitude';
import { Send2OutLineIcon } from 'utils/icons/figmaIcons';
import { useAIChatContext } from './AIChat.context';
import AIChatAttachements from './AIChatAttachements';
import Dictaphone from './Dictaphone';

type AIChatTextboxProps = {
  isSmall?: boolean;
  className?: string;
  placeholder?: string;
  wrapperClassName?: string;
};

const buttonAnimationProps = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.9 },
  transition: { duration: 0.3, ease: 'easeOut' },
};

const AIChatTextbox = ({
  isSmall,
  className,
  placeholder,
  wrapperClassName,
}: AIChatTextboxProps) => {
  const { t } = useTranslation();
  const { handleSend, setMessage, message, usage, attachmentApi } =
    useAIChatContext();

  const disabled = usage.reachedMaxCount || usage.reachedMaxMonthlyCount;

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!message?.trim()?.length) return;
      if (e.key === 'Enter' && e.shiftKey) return;
      if (e.code === 'Enter') {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend, message],
  );

  const openFilePicker = useCallback(() => {
    attachmentApi?.open();
  }, [attachmentApi]);

  return (
    <div
      className={classNames(
        'rounded-xl  border border-primary-100 relative',
        {
          'px-2 border-primary-100': isSmall,
          'px-3 bg-primary-50 border-primary-50': !isSmall,
        },
        wrapperClassName,
      )}
    >
      {attachmentApi.enabled && <AIChatAttachements />}

      <div className="flex items-center ">
        {attachmentApi.enabled && (
          <Tooltip content={t('expenses.dropzone.cta2')}>
            <Button
              iconOnly
              structure="text"
              onClick={openFilePicker}
              disabled={disabled}
            >
              <AttachmentsIcon className="h-5 w-5" />
            </Button>
          </Tooltip>
        )}

        <textarea
          id={'ai-assistant-textarea'}
          disabled={disabled}
          value={message}
          rows={message?.includes('\n') || message?.length > 50 ? 2 : 1}
          onKeyDown={onKeyDown}
          onClick={() => logAmplitude(WEB_AIASSISTANT_CLICKTEXTAREA)}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={placeholder || t('ai_send_placeholder')}
          className={classNames(
            'border-none placeholder:text-primary-400 text-sm !text-primary outline-none resize-none w-full flex-1',
            { '!bg-primary-50': !isSmall },
            className,
          )}
        />

        <AnimatePresence mode="wait">
          {/* Keep Dictaphone mounted so transcript updates continue even after text appears */}
          {attachmentApi.enabled && !disabled ? (
            <motion.div
              key="dictaphone"
              {...buttonAnimationProps}
              className="absolute right-2 z-10 items-center justify-center"
            >
              <Dictaphone
                disabled={usage.reachedMaxCount}
                hasMessage={!!message.length}
              />
            </motion.div>
          ) : null}

          {message.length || !attachmentApi.enabled ? (
            <motion.div
              key="send-button"
              {...buttonAnimationProps}
              className="absolute right-2 items-center justify-center"
            >
              <Button
                disabled={disabled || !message?.trim()}
                onClick={() => handleSend()}
                asDefault
                // TODO: change to theme color when the new DS is ready
                className={classNames(
                  ' hover:opacity-80 transition-opacity duration-300 group',
                  {
                    'p-2 rounded-full bg-[#1B1B1B]': !isSmall,
                    'p-0.5': isSmall,
                  },
                )}
              >
                {isSmall ? (
                  <Send2OutLineIcon className="w-6 h-6" />
                ) : (
                  <ArrowUp
                    className="w-5 h-5 stroke-white text-white group-disabled:opacity-50 transition-opacity duration-300"
                    fill="transparent"
                  />
                )}
              </Button>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
};

const DropzoneComponent: FC<AIChatTextboxProps> = (props) => {
  const { t } = useTranslation();
  const { attachmentApi } = useAIChatContext();

  if (!attachmentApi.enabled) {
    return <AIChatTextbox {...props} />;
  }

  return (
    <div {...attachmentApi.dropzoneProps.root()} className="relative">
      <AIChatTextbox {...props} />
      <AnimatePresence mode="wait">
        {attachmentApi.dropzoneProps.isDragActive && (
          <motion.div
            key="dropzone-active"
            className="absolute inset-0 bg-primary-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <p className="text-primary-400 text-sm">
              {t('expenses.dropzone.cta')}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DropzoneComponent;
